import * as a from '@mitranim/js/all.mjs'
import * as c from './conf.mjs'
import {E} from './util-view.mjs'
import * as ut from './util-text.mjs'
import * as uf from './util-form.mjs'
import * as um from './util-misc.mjs'

export function validatePassword(val) {
  return !a.isStr(val) || val.length < c.PASSWORD_LENGTH_MIN
    ? `Password should be at least ${c.PASSWORD_LENGTH_MIN} symbols`
    : undefined
}

// Must be kept in sync with email validation on the backend.
export const RE_EMAIL = /^[\w.+-]{1,64}@(?=.{1,255}$)(?:[\w-]+\.)*[\w-]+$/

export function isValidEmail(val) {
  return a.isStr(val) && RE_EMAIL.test(val)
}

export function isValidUrl(val) {return !!a.toUrl(val).origin}

/*
If the URL is invalid, this panics with a descriptive error.
Allows partial URLs such as nil, empty string, or `/blah`.
*/
export function validateUrlPermissive(val) {a.url(val)}

// TODO cross-language.
const PLEASE_FILL_FIELD = `Please fill out this field`

export const validateFieldNotEmpty = validatorFieldNotEmpty(PLEASE_FILL_FIELD)

export function validatorFieldNotEmpty(msg) {
  a.reqValidStr(msg)
  return function validateFieldNotEmpty(val) {
    return a.vac(!uf.reqField(val).hasValue()) && msg
  }
}

export function validatorFieldHasFile(msg) {
  return function validatorFieldHasFile(field) {
    const file = field.getFile()?.getDomFile()
    return a.vac(!file) && msg
  }
}

export function validateFieldCoordNotEmpty(val) {
  return a.vac(!val.hasPoint()) && `Please select address`
}

export function validatorFieldTextMinLength(minLen) {
  return a.bind(validateFieldTextMinLength, a.reqNat(minLen))
}

export function validatorFieldTextMaxLength(maxLen) {
  return a.bind(validateFieldTextMaxLength, a.reqNat(maxLen))
}

function fieldTextLength(len, field) {
  a.reqNat(len)
  uf.reqField(field)

  const val = field.getValueSingle()
  if (!a.isStr(val)) return undefined

  return a.lenUni(val)
}

function validateFieldTextMinLength(minLen, field) {
  const len = fieldTextLength(minLen, field)
  if (len < minLen) return `Min length not reached: ${len} of ${minLen}, remains: ${minLen - len}`
  return undefined
}

function validateFieldTextMaxLength(maxLen, field) {
  const len = fieldTextLength(maxLen, field)
  if (len > maxLen) return `Max length exceeded: ${len} of ${maxLen}, overflow: ${len - maxLen} `
  return undefined
}

export function validateFieldPassword(val) {
  return validatePassword(uf.reqField(val).getValueSingle())
}

// TODO cross-language.
export function validatorFieldPasswordMatch(key) {
  a.reqKey(key)

  return function validateFieldPasswordMatch(field0) {
    const field1 = uf.reqField(field0).form.getField(key)
    if (!field1 || field0.getValueSingle() !== field1.getValueSingle()) {
      return `Password and password confirmation don't match`
    }
    return undefined
  }
}

export const validateFieldImageFileNotEmpty = validatorFieldNotEmpty(`Please select an image`)
export const validateFieldBookFileNotEmpty = validatorFieldNotEmpty(`Please select a book`)
export const validateFieldVidNotEmpty = validatorFieldHasFile(`Please select a video`)

export function validateFieldOptionalEmail(field) {
  if (!uf.reqField(field).getValueSingle()) return undefined
  return validateFieldEmail(field)
}

export function validateFieldEmail(field) {
  const val = uf.reqField(field).getValueSingle()
  return isValidEmail(val) ? undefined : `Please enter a valid email`
}

export function validateFieldNonEmail(field) {
  const val = uf.reqField(field).getValueSingle()
  return isValidEmail(val) ? `Please enter a non-email` : undefined
}

// TODO rename to `validateFieldUrlOpt`.
export function validateFieldOptionalUrl(field) {
  if (!uf.reqField(field).getValueSingle()) return undefined
  return validateFieldUrl(field)
}

// Currently doesn't allow leading or trailing whitespace. Should we allow it?
// Should we trim it on the input level?
export function validateFieldUrl(field) {
  const value = uf.reqField(field).getValueSingle()
  if (isValidUrl(value)) return undefined
  return `Please enter a valid URL, starting with https:// or http://`
}

export function validateFieldMultiselectNotEmpty(field) {
  return a.vac(!uf.reqField(field).hasLen()) && `Please select at least one`
}

export function validatorFieldMultiselectBetween(min, max) {
  a.reqNat(min)
  a.optNum(max)

  if (a.isNil(max)) return a.nop

  return function validateFieldMultiselectBetween(field) {
    const len = uf.reqField(field).len()
    if (!len && min) {
      return `Please select at least one`
    }
    if (!(len >= min)) {
      return `Please select at least ${min}`
    }
    if (!(len <= max)) {
      return `Please select no more than ${max}`
    }
    return undefined
  }
}

export function validateServiceGeoFieldOpt(field) {
  if (field.form.fields.isOnline?.getValueSingle()) return undefined
  if (field.hasValue()) return undefined
  return PLEASE_FILL_FIELD
}

/*
TODO consider renaming from "len" to "count" for consistency with the definition
of `UiValidation` on the server.
*/
export function validateFieldLen(max) {
  a.reqIntPos(max)
  return function validatorFieldLen(field)  {
    return a.vac(uf.reqField(field).len() > max) && `Cannot contain more than ${max} items`
  }
}

export function validateFileExt(exts) {
  um.reqStrArr(exts)

  return function validatorFileExt(field) {
    for (const val of uf.reqField(field).getFiles()) {
      const ext = ut.extClean(val.file?.name)
      if (a.includes(exts, ext)) continue
      return `Allowed file extensions: "${ut.extsToFileInputAccept(exts)}", got: "${ext}"`
    }
    return undefined
  }
}

export async function validatorFieldNotFreePrice() {
  const btn = await um.imp(import(`./views/buttons.mjs`))

  const text = `Please note that with your Free Membership Plan you can only list free services. Please select the price ‘FREE’ or upgrade your Membership if you’d like to start selling.`

  function render() {
    return E(
      btn.Control,
      {href: c.LINK_EMBED_BUY_MEMBERSHIP, class: `decorate-link`},
      text,
    )
  }

  const tar = render()

  // Allows conversion to Preact markup. See `shims/react.mjs`.
  tar.render = render

  // Allows to display this element either as fancy markup,
  // or stringified as field error text.
  tar.toString = a.val(text)

  return function validatorFieldNotFreePrice(val) {
    const value = uf.reqField(val).value
    return a.vac(value !== 0) && tar
  }
}

export function validateFieldNoWhitespace(field) {
  const val = a.laxStr(uf.reqField(field).getValueSingle())
  if (/\s/.test(val)) return `unexpected whitespace in ` + a.show(val)
  return undefined
}
